import { useQuery, useQueryClient } from 'react-query';
import { Http } from '../../services/Http';
import { QUERY_KEYS } from '../constants';

/** A person reference as returned by the approval-delegate API (id + display name). */
export interface ApprovalDelegatePerson {
  id: number;
  name: string;
}

/** One delegation row: a deal's approval re-routed from one deal owner to another, for one OP. */
export interface ApprovalDelegate {
  id: number;
  stage: string;
  deal: { id: number; name: string };
  operatingPartner: ApprovalDelegatePerson;
  fromDealOwner: ApprovalDelegatePerson;
  toDealOwner: ApprovalDelegatePerson;
}

async function fetchApprovalDelegates(): Promise<ApprovalDelegate[]> {
  const { data } = await Http.axios.get<ApprovalDelegate[]>('/approval-delegate');
  return data;
}

/** Admin-only list of all approval delegations (grouped/collapsed client-side by source deal owner). */
export const useApprovalDelegates = () => {
  return useQuery([QUERY_KEYS.APPROVAL_DELEGATES], fetchApprovalDelegates, {
    staleTime: Infinity,
    refetchInterval: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });
};

export const useInvalidateApprovalDelegates = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.APPROVAL_DELEGATES });
    // Delegations change who must approve open timesheets, so the deal-owner views must refresh too.
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MONTHLY_TIMESHEET });
  };
};
